/**
 * uni-push 通用插件 —— 类型定义
 * 本目录不依赖任何项目代码（store/utils/api），可整体复制到其他 uni-app 项目使用
 */

/** 推送事件类型 */
export enum PushEventType {
  /** 用户点击通知栏 */
  CLICK = 'click',
  /** App 在前台收到透传消息 */
  RECEIVE = 'receive',
}

/** 推送初始化状态 */
export type PushInitStatus = 'idle' | 'pending' | 'success' | 'failed';

/** 标准化后的推送消息 */
export interface PushMessage {
  title?: string
  content?: string
  /** 业务数据，兼容字符串(JSON)/对象两种后端下发格式 */
  payload?: string | PushPayloadData
  /** 消息 ID */
  id?: string
  /** 扩展字段 */
  [key: string]: any
}

/** payload 解析后的业务数据结构（各项目可通过扩展字段自由约定） */
export interface PushPayloadData {
  /** 业务类型，如 order / chat / system / web */
  type: string
  /** 业务 ID（如订单号、会话 ID） */
  id?: string
  /** 跳转链接（type=web 等场景） */
  url?: string
  /** 额外动作标识 */
  action?: string
  /** 扩展字段 */
  [key: string]: any
}

/** 插件内部维护的运行时状态（通过 onStateChange 同步给宿主项目） */
export interface PushState {
  clientId: string
  initStatus: PushInitStatus
  initError: string
  isListening: boolean
}

/**
 * 透传消息（receive）处理器
 * @param data 解析后的 payload（解析失败/无 payload 时为 null）
 * @param msg 原始消息
 */
export type PushReceiveHandler = (data: PushPayloadData | null, msg: PushMessage) => void;

/** 本地补偿通知的内容（uni.createPushMessage 参数子集） */
export interface PushLocalNotification {
  title: string
  content: string
  payload?: string | PushPayloadData
}

/**
 * 通知提醒配置（单条消息级，由后端随 payload 下发，字段缺省均为 false/空）
 */
export interface PushAlertOptions {
  /** 播放系统默认消息提示音，默认 false；设置了 customSound 时此项被忽略 */
  sound?: boolean
  /** 自定义声音文件路径（宿主项目 static 资源），默认空 = 不使用自定义声音 */
  customSound?: string
  /** 自定义声音强制播放（绕过宿主项目设置页的「提示音」开关），默认 false */
  forceSound?: boolean
  /** 震动，默认 false */
  vibrate?: boolean
}

/**
 * App 内推送提醒开关（宿主项目设置页维护，作为全局闸门注入插件）
 * 关闭后，即使消息请求了声音/震动也不播放（forceSound 的自定义声音除外）
 */
export interface PushAlertSettings {
  /** 提示音总开关 */
  soundEnabled: boolean
  /** 震动总开关 */
  vibrateEnabled: boolean
}

/** 通知提醒插件配置（配置后，receive 消息时自动按 payload 提醒字段播放声音/震动） */
export interface PushAlertConfig {
  /** 注入 App 内设置开关（不传或字段缺省视为开启） */
  getSettings?: () => Partial<PushAlertSettings>
}

/**
 * 插件配置项 —— 各项目在自己的配置文件中按业务编写
 */
export interface UniPushOptions {
  /** 兜底首页路径（点击消息无法解析路由时跳转），tabbar 页需可被 switchTab */
  homePage: string

  /**
   * 根据 payload 构建点击跳转路径（项目路由映射写在这里）
   * 返回空字符串表示无法识别，走 homePage 兜底
   */
  resolveRoute?: (data: PushPayloadData) => string

  /**
   * 点击消息的自定义处理（在默认跳转之前调用）
   * 返回 true 表示已完全接管，跳过默认的路由跳转逻辑
   */
  onClick?: (data: PushPayloadData | null, msg: PushMessage) => boolean | void

  /**
   * 透传消息(receive)按业务类型分发的处理器表，key 为 payload.type
   * 未匹配到的类型走 onReceive 兜底
   */
  receiveHandlers?: Record<string, PushReceiveHandler>

  /** 透传消息兜底处理（无 payload / 未匹配 receiveHandlers 时调用） */
  onReceive?: PushReceiveHandler

  /**
   * App 在线时是否创建本地补偿通知（Android 在线收到 notification 消息不触发 receive，
   * 透传消息在线时不会自动展示，需 uni.createPushMessage 补偿）
   * - true（默认）：使用消息自带 title/content 创建
   * - false：不创建
   * - 函数：自定义通知内容，返回 false 表示本条不创建
   */
  createNotification?: boolean | ((msg: PushMessage, data: PushPayloadData | null) => PushLocalNotification | false)

  /**
   * 通知权限请求（Android 13+ 默认拒绝通知权限）
   * - false：不检查、不请求
   * - 默认（不传）：仅在「从未请求过」时触发一次系统弹框；检测到用户拒绝后永不再主动弹框
   *   （商店合规要求，不做本地缓存），由用户在 App「设置 -> 消息通知 / 系统权限管理」自行开启
   */
  permissionPrompt?: false

  /** 获取到 clientId 后回调（常见用途：上报 cid 给后端绑定用户） */
  onClientId?: (cid: string) => void

  /** 插件状态变化回调（用于同步到 pinia/vuex 等宿主状态管理） */
  onStateChange?: (state: PushState) => void

  /** 收到任意消息的原始钩子（记录 lastMessage、埋点等），在分发处理之前调用 */
  onMessage?: (msg: PushMessage, type: PushEventType, data: PushPayloadData | null) => void

  /** 自定义跳转方法（默认 navigateTo，失败依次降级 switchTab / reLaunch） */
  navigate?: (path: string) => void

  /** clientId 本地缓存 key，默认 'push_client_id' */
  clientIdCacheKey?: string

  /**
   * 通知提醒（声音/震动）：配置后，前台/锁屏/后台在线收到 receive 消息时，
   * 自动按 payload 的 sound/customSound/forceSound/vibrate 字段提醒；不配置则完全不提醒
   */
  alert?: PushAlertConfig

  /** 是否输出调试日志，默认 true */
  debug?: boolean
}
